<script setup lang="ts">
import type { Component } from 'vue';
import Avatar from 'primevue/avatar';
import BoxIcon from '@primeicons/vue/box';
import TextTruncate from '@apparatix/library/TextTruncate.vue';

/**
 * Visual mirror of AssociationListItem for inventory-domain rows (structure,
 * lease, landowner, payees). Kept separate from AssociationListItem so it can
 * carry non-CRM row types without widening the shared `associationType` union.
 * Emits `click` when the row is interactive (so it can later open its own flyout).
 */
type Props = {
  title: string;
  subtitle?: string;
  icon?: Component;
  iconBackground?: string;
  iconColor?: string;
  clickable?: boolean;
  border?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  subtitle: undefined,
  icon: BoxIcon,
  iconBackground: 'var(--p-sky-100)',
  iconColor: 'var(--p-sky-600)',
  clickable: false,
  border: false,
});

const emit = defineEmits<{ (e: 'click'): void }>();
</script>
<template>
  <div class="flex flex-col gap-2" :class="[props.border ? 'border-b border-[var(--neutral-background)] pb-2' : '']">
    <div class="flex items-center gap-4">
      <div class="flex-none w-12">
        <Avatar
          v-if="props.icon"
          size="large"
          shape="circle"
          :style="{ backgroundColor: props.iconBackground }"
        >
          <template #icon>
            <component :is="props.icon" :color="props.iconColor" />
          </template>
        </Avatar>
      </div>
      <div
        class="flex-initial w-72 flex flex-col gap-1"
        :class="[props.clickable ? 'cursor-pointer' : '']"
        @click="props.clickable && emit('click')"
      >
        <TextTruncate :text="props.title" class="text-sm font-medium text-[var(--swimlane-primary-text)] leading-snug" />
        <TextTruncate v-if="props.subtitle" :text="props.subtitle" class="text-xs font-normal text-[var(--swimlane-secondary-text)] leading-snug" />
        <span v-else>&nbsp;</span>
      </div>
      <div class="flex-auto">
        <slot />
      </div>
    </div>
  </div>
</template>
